/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { extractErrorMessage } from '../../utils/error-handler';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import ScreenHeader from '../../components/common/screen-header';
import { valueSetsService } from '../../services/value-sets.service';
import { projectsService } from '../../services/projects.service';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import styles from './value-set-form.module.css';

// Zod Validation Schema with dynamic labels (mirrors backend ValueSet schemas)
const valueSetItemSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .max(64, 'Code must not exceed 64 characters')
    .regex(
      /^[A-Z][A-Z0-9_-]{0,63}$/,
      'Code must be SCREAMING_SNAKE_CASE (start with a letter; A-Z, 0-9, _ and - only)'
    ),
  labels: z
    .record(z.string(), z.string())
    .refine((labels) => Object.keys(labels).length > 0, {
      message: 'At least one label is required',
    })
    .refine((labels) => !!(labels.en && labels.en.trim()), {
      message: 'An English (en) label is required',
    })
    .refine((labels) => Object.values(labels).every((v) => (v ?? '').length <= 256), {
      message: 'Each label must not exceed 256 characters',
    }),
});

const valueSetSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1, 'Key is required')
    .max(120, 'Key must not exceed 120 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Key may contain only alphabets and spaces'),
  description: z
    .string()
    .max(1000, 'Description must not exceed 1000 characters')
    .trim()
    .optional()
    .or(z.literal('')),
  items: z
    .array(valueSetItemSchema)
    .min(1, 'At least one item is required')
    .max(500, 'A value set may contain at most 500 items'),
  appId: z.string().trim().optional().or(z.literal('')),
});

type ValueSetFormData = z.infer<typeof valueSetSchema>;

const ValueSetForm = () => {
  const navigate = useAppNavigate();
  const { key } = useParams<{ key: string }>();
  const isEditMode = Boolean(key);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Stops autosave rewriting the draft after a submit clears it (the reason the
  // "restore?" banner reappeared right after a create/update).
  const submittedRef = useRef(false);
  // Gate saving on the matching value-set permission (create on new, update on edit).
  const canCreate = useCanCreate(JOB_NAMES.VALUE_SETS);
  const canUpdate = useCanUpdate(JOB_NAMES.VALUE_SETS);
  const canSave = isEditMode ? canUpdate : canCreate;

  const DRAFT_KEY = `value-set-draft-${isEditMode ? key : 'new'}`;

  // Draft state
  const [draftData, setDraftData] = useState<ValueSetFormData | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // Track which items have the "add more label" row visible
  const [showAddLabelRow, setShowAddLabelRow] = useState<Record<number, boolean>>({});

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ValueSetFormData>({
    resolver: zodResolver(valueSetSchema),
    defaultValues: {
      key: '',
      description: '',
      items: [{ code: '', labels: {} }],
      appId: '',
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // Navigation blocker for unsaved changes
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const formValues = watch();

  // Fetch value set data for edit mode
  const { data: valueSet, isLoading: fetchLoading } = useQuery({
    queryKey: ['value-sets', key, 'full'],
    queryFn: () => valueSetsService.getByKeyFull(key!),
    enabled: isEditMode && !!key,
  });

  // Set breadcrumb title to show value set key instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? valueSet?.key : null);

  // Populate form when value set data is loaded
  useEffect(() => {
    if (valueSet && isEditMode) {
      // Transform items: backend might return either 'label' (string) or 'labels' (object)
      const transformedItems = valueSet.items && valueSet.items.length > 0
        ? valueSet.items.map((item: any) => {
            // Check if item has 'label' (single string) or 'labels' (object)
            if (item.label && typeof item.label === 'string') {
              // Backend returned localized format - convert to full format
              return {
                code: item.code,
                labels: { en: item.label }
              };
            } else if (item.labels && typeof item.labels === 'object') {
              // Backend returned full format - use as is
              return {
                code: item.code,
                labels: item.labels
              };
            } else {
              // Fallback
              return {
                code: item.code || '',
                labels: {}
              };
            }
          })
        : [{ code: '', labels: {} }];

      const formData = {
        key: valueSet.key || '',
        description: valueSet.description || '',
        items: transformedItems,
        appId: (valueSet as any).appId || '',
      };
      reset(formData);
    }
  }, [valueSet, isEditMode, reset]);

  // Draft autosave (only for create mode)
  useEffect(() => {
    if (isEditMode || !isDirty || submittedRef.current) return;

    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);

    return () => clearTimeout(timer);
  }, [formValues, isDirty, isEditMode, DRAFT_KEY]);

  // Load draft on mount (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch (error) {
          console.error('Failed to parse draft:', error);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Mutation for create/update
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && key) {
        return valueSetsService.update(key, data);
      }
      return valueSetsService.create(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['value-sets'] });

      // Also refresh the cache for this value set on the server
      if (data?.key) {
        valueSetsService.refreshCache(data.key).catch(err =>
          console.warn('Failed to refresh cache:', err)
        );
      }

      localStorage.removeItem(DRAFT_KEY);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Value Set ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });

      // Increased delay to ensure snackbar is visible
      setTimeout(() => {
        navigate('/value-sets');
      }, 2000);
    },
    onError: (error: any) => {
      submittedRef.current = false;
      console.error('Error response:', error.response);
      const errorMessage = extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} value set`);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = mutation.isPending;

  const onSubmit = (data: ValueSetFormData) => {
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    try {
      // appId is only honoured on create; updates must not change the owning project.
      const { appId, ...rest } = data;
      const payload: any = { ...rest };
      if (!isEditMode && appId) payload.appId = appId;
      mutation.mutate(payload);
    } catch (error) {
      console.error('Error calling mutation.mutate:', error);
    }
  };

  // Check if there's pending label input value (for validation bypass)
  const hasPendingLabelValue = (itemIndex: number): boolean => {
    const labelInput = document.getElementById(`new-label-${itemIndex}`) as HTMLInputElement;
    return !!(labelInput?.value?.trim());
  };

  // Handle form submission - auto-add pending labels before submit
  const handleFormSubmit = () => {
    // First, auto-add any pending labels
    let hasAddedLabels = false;
    formValues.items.forEach((item, itemIndex) => {
      const hasNoLabels = !item.labels || Object.keys(item.labels).length === 0;
      const langInput = document.getElementById(`new-lang-${itemIndex}`) as HTMLInputElement;
      const labelInput = document.getElementById(`new-label-${itemIndex}`) as HTMLInputElement;

      const langCode = hasNoLabels ? 'en' : langInput?.value?.trim();
      const labelValue = labelInput?.value?.trim();

      // If there's a pending label value, auto-add it
      if (langCode && labelValue) {
        handleAddLabel(itemIndex, langCode, labelValue);
        if (labelInput) labelInput.value = '';
        if (!hasNoLabels && langInput) langInput.value = '';
        hasAddedLabels = true;
      }
    });

    // If we added labels, wait for state update then submit
    if (hasAddedLabels) {
      setTimeout(() => {
        handleSubmit(onSubmit)();
      }, 50);
    } else {
      // No pending labels, submit directly
      handleSubmit(onSubmit)();
    }
  };

  const handleRestoreDraft = () => {
    if (draftData) {
      reset(draftData);
      setShowDraftBanner(false);
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftData(null);
    setShowDraftBanner(false);
  };

  const handleAddItem = () => {
    append({ code: '', labels: {} });
  };

  const handleRemoveItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Add a new language label to a specific item
  const handleAddLabel = (itemIndex: number, langCode: string, labelValue: string) => {
    const currentLabels = formValues.items[itemIndex].labels || {};
    setValue(`items.${itemIndex}.labels`, {
      ...currentLabels,
      [langCode]: labelValue
    }, { shouldDirty: true, shouldValidate: true });
  };

  // Remove a language label from a specific item
  const handleRemoveLabel = (itemIndex: number, langCode: string) => {
    const currentLabels = { ...formValues.items[itemIndex].labels };
    delete currentLabels[langCode];
    setValue(`items.${itemIndex}.labels`, currentLabels, { shouldDirty: true, shouldValidate: true });
  };

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-ValueSet-Form">
        {/* Header - Compact */}
        <ScreenHeader
          icon="pi pi-cog"
          title={isEditMode ? 'Edit Value Set' : 'Create New Value Set'}
          subtitle={isEditMode ? 'Update value set configuration and enumeration items' : 'Add a new value set with enumeration values'}
          onBack={() => handleNavigate('/value-sets')}
          right={
            <>
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                outlined
                onClick={() => handleNavigate('/value-sets')}
                disabled={isSaving}
              />
              {canSave && (
                <Button
                  type="button"
                  label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                  icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  onClick={handleFormSubmit}
                  disabled={isSaving}
                />
              )}
            </>
          }
        />
          {/* Draft Recovery Banner */}
          {showDraftBanner && (
            <div className={styles.draftBanner}>
              <div className={styles.draftBannerContent}>
                <i className={`pi pi-exclamation-triangle ${styles.draftBannerIcon}`} />
                <span className={styles.draftBannerText}>
                  You have unsaved changes from a previous session. Would you like to restore them?
                </span>
              </div>
              <div className={styles.draftBannerActions}>
                <Button label="Restore" size="small" onClick={handleRestoreDraft} />
                <Button label="Discard" size="small" severity="secondary" outlined onClick={handleDiscardDraft} />
              </div>
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
            {/* Basic Information */}
            <div className="ds-card p-4 mb-3">
              <h3 className="text-sm font-semibold text-ink mb-3 flex items-center gap-1.5">
                <i className="pi pi-info-circle text-blue-600" style={{ fontSize: '0.875rem' }} />
                Basic Information
              </h3>

              <div className={styles.formGrid}>
                <div className={styles.fieldWrapper}>
                  <label className={styles.label}>
                    Key <span className={styles.required}>*</span>
                  </label>
                  <Controller
                    name="key"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="e.g., ERROR_SEVERITY"
                        error={errors.key?.message}
                        disabled={isEditMode}
                      />
                    )}
                  />
                </div>

                <div className={styles.fieldWrapper}>
                  <label className={styles.label}>Description</label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        placeholder="Brief description of this value set"
                        rows={2}
                        error={errors.description?.message}
                      />
                    )}
                  />
                </div>

                <div className={styles.fieldWrapper}>
                  <Controller
                    name="appId"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect
                        {...field}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label="Project"
                        options={projectOptions}
                        placeholder="Select project"
                        disabled={projectLocked || isEditMode}
                        error={!!errors.appId}
                        helperText={errors.appId?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Enumeration Items */}
            <div className="ds-card p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-ink flex items-center gap-1.5">
                  <i className="pi pi-list text-blue-600" style={{ fontSize: '0.875rem' }} />
                  Enumeration Items
                </h3>
                <Button
                  type="button"
                  label="Add Item"
                  icon="pi pi-plus"
                  size="small"
                  onClick={handleAddItem}
                />
              </div>

              {errors.items && !Array.isArray(errors.items) && (
                <div className={styles.errorMessage}>
                  {errors.items.message}
                </div>
              )}

              <div className={styles.itemsContainer}>
                {fields.map((field, itemIndex) => (
                  <div key={field.id} className={styles.itemCard}>
                    <div className={styles.itemHeader}>
                      <h4 className={styles.itemTitle}>Item {itemIndex + 1}</h4>
                      {fields.length > 1 && (
                        <button aria-label="Delete"
                          type="button"
                          onClick={() => handleRemoveItem(itemIndex)}
                          className={styles.deleteItemButton}
                        >
                          <i className="pi pi-trash" />
                        </button>
                      )}
                    </div>

                    {/* Code and Labels Grid */}
                    <div className={styles.itemContent}>
                      {/* Code Field */}
                      <div className={styles.itemFieldWrapper}>
                        <label className={styles.label}>
                          Code <span className={styles.required}>*</span>
                        </label>
                        <Controller
                          name={`items.${itemIndex}.code`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="e.g., ERROR"
                              error={(errors.items?.[itemIndex] as any)?.code?.message}
                            />
                          )}
                        />
                      </div>

                      {/* Labels Section */}
                      <div className={styles.labelsSection}>
                        <label className={styles.label}>
                          Labels (Multi-language, Click plus) <span className={styles.required}>*</span>
                        </label>

                        {/* Existing Labels */}
                        {formValues.items[itemIndex]?.labels && Object.keys(formValues.items[itemIndex].labels).length > 0 && (
                          <div className={styles.labelsGrid}>
                            {Object.entries(formValues.items[itemIndex].labels).map(([langCode, labelValue]) => (
                              <div key={langCode} className={styles.labelRow}>
                                <Input
                                  value={langCode}
                                  disabled
                                  placeholder="Lang"
                                  onChange={() => {}}
                                />
                                <Input
                                  value={labelValue}
                                  onChange={(e: any) => {
                                    const newValue = typeof e === 'string' ? e : e?.target?.value ?? e;
                                    handleAddLabel(itemIndex, langCode, newValue);
                                  }}
                                  placeholder="e.g., Error, त्रुटि, లోపం"
                                />
                                {/* Always show remove button */}
                                <button
                                  type="button"
                                  aria-label="Remove label"
                                  onClick={() => handleRemoveLabel(itemIndex, langCode)}
                                  className={styles.labelRemoveButton}
                                >
                                  <i className="pi pi-times" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add New Label Row */}
                        {(() => {
                          const hasNoLabels = !formValues.items[itemIndex]?.labels || Object.keys(formValues.items[itemIndex].labels).length === 0;
                          const isAddRowVisible = showAddLabelRow[itemIndex] || false;

                          // Show add row when: no labels exist OR user clicked plus to add more
                          if (!hasNoLabels && !isAddRowVisible) {
                            // Show just a plus button to add more languages
                            return (
                              <button
                                type="button"
                                className={styles.addLanguageButton}
                                onClick={() => {
                                  setShowAddLabelRow(prev => ({ ...prev, [itemIndex]: true }));
                                }}
                                aria-label="Add more languages"
                              >
                                <i className="pi pi-plus" />
                              </button>
                            );
                          }

                          return (
                            <div className={styles.addLabelRow}>
                              <Input
                                id={`new-lang-${itemIndex}`}
                                placeholder="lang"
                                value={hasNoLabels ? 'en' : undefined}
                                defaultValue={hasNoLabels ? undefined : ''}
                                disabled={hasNoLabels}
                                key={`lang-${itemIndex}-${hasNoLabels ? 'first' : 'other'}`}
                                onChange={() => {}}
                              />
                              <Input
                                id={`new-label-${itemIndex}`}
                                placeholder="Enter label text"
                                onChange={() => {}}
                              />
                              <Button
                                type="button"
                                icon="pi pi-plus"
                                size="small"
                                onClick={() => {
                                  const langInput = document.getElementById(`new-lang-${itemIndex}`) as HTMLInputElement;
                                  const labelInput = document.getElementById(`new-label-${itemIndex}`) as HTMLInputElement;
                                  const langCode = hasNoLabels ? 'en' : langInput?.value.trim();
                                  const labelValue = labelInput?.value.trim();

                                  if (langCode && labelValue) {
                                    handleAddLabel(itemIndex, langCode, labelValue);
                                    if (labelInput) labelInput.value = '';
                                    if (!hasNoLabels && langInput) langInput.value = '';
                                    // Hide the add row after adding
                                    setShowAddLabelRow(prev => ({ ...prev, [itemIndex]: false }));
                                  }
                                }}
                                aria-label="Add label"
                              />
                            </div>
                          );
                        })()}

                        {(errors.items?.[itemIndex] as any)?.labels && !hasPendingLabelValue(itemIndex) && (
                          <small className="text-red-500 text-xs mt-1">
                            {(errors.items?.[itemIndex] as any)?.labels?.message || 'At least one label is required'}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
      </div>

      {/* Leave Confirmation Dialog */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="ValueSetForm.Dialog.Leave"
      />
    </>
  );
};

export default ValueSetForm;
